import React, { ReactElement, useState, useCallback, useMemo, useEffect } from "react";
import {
    Drawer,
    DrawerBody,
    DrawerFooter,
    DrawerHeader,
    DrawerHeaderTitle,
} from "src/components/Drawer";
import { QualityHubActionFormDrawerProps, QualityHubActionFormDrawerState, QualityHubActionFormDrawerStateProps } from "./QualityHubActionFormDrawer.types";
import { createTaskListActionAsync, createTaskListItemAsync, updateTaskListItemAsync } from "src/api";
import { TaskActionModelUpdateHandler, TaskActionFormModel } from "../types";
import { QualityHubActionForm } from "../QualityHubActionForm";
import { localize, localizeFormat } from "src/l10n";
import { SpintrTypes } from "src/typings";
import { UnstyledButton } from "src/ui";
import { SmallBodyBolder } from "src/components/Text";
import { PrimaryButton } from "@fluentui/react";
import { useDispatch, useSelector } from "react-redux";
import { taskListItemCreated, taskListItemUpdated } from "src/redux";
import { Conditional } from "src/components/Conditional";
import SpintrLoader from "src/ui/components/Loader";
import { setConfirmPopup } from "src/popups/actions";

const initialModel: TaskActionFormModel = {
    title: "",
    description: "",
    sortOrder: 0,
    contentStatus: SpintrTypes.ContentStatus.Published,
    assignees: [],
};

/**
 * QualityHubActionFormDrawer component
 *
 * @param {QualityHubActionFormDrawerProps} props - The component props
 * @returns {ReactElement | null} The rendered component
 */
function QualityHubActionFormDrawer({
    action,
    listId,
    onDismiss,
    open,
    task,
}: QualityHubActionFormDrawerProps): ReactElement | null {
    const {
        isSmallViewMode,
        currentUser,
        taskList,
    } = useSelector<Spintr.AppState, QualityHubActionFormDrawerStateProps>(
        (appState) => ({
            isSmallViewMode: appState.ui.isSmallViewMode,
            currentUser: appState.profile.active,
            taskList: appState.qualityHub.listsById[listId ?? action?.listId],
        }),
    );

    const isOwnerOfList = useMemo(() => {
        if (!currentUser || !taskList) {
            return false;
        }

        const ownerIds = [
            currentUser.id,
            currentUser.department?.id,
            currentUser.department?.office?.id,
            currentUser.department?.office?.company?.id,
        ].filter((id): id is number => !!id);

        return ownerIds.some(
            (ownerId) => (taskList.targets ?? [])
                .some((target) => target.id === ownerId));
    }, [taskList?.targets, currentUser]);

    const isLeader = currentUser?.leadsDepartment as boolean | undefined;

    const dispatch = useDispatch();

    const [model, setModel] = useState<TaskActionFormModel>({
        ...initialModel,
        assignees: [],
        contentStatus: isOwnerOfList
            ? SpintrTypes.ContentStatus.Published
            : SpintrTypes.ContentStatus.Draft,
    });

    const [state, setState] = useState<QualityHubActionFormDrawerState>({
        isSaving: false,
    });
    const { isSaving } = state;

    const l10n = useMemo(() => ({
        createAction: localize("QH_CREATE_ACTION"),
        cancel: localize("Avbryt"),
        save: localize("Spara"),
    }), []);

    const onModelChanged = useCallback<TaskActionModelUpdateHandler>(
        (updater) => setModel((model) => updater(model)),
        [setModel]);

    const onDismissing = useCallback(() => {
        if (task && !action) {
            dispatch(setConfirmPopup({
                isOpen: true,
                message: localizeFormat("FOLLOW_UP_ABORTED_WARNING_F", task.title),
                onConfirm: () => {
                    setModel(initialModel);
                    onDismiss(false);
                }
            }));
            return;
        }

        setModel(initialModel);
        onDismiss(false);
    }, [onDismiss, setModel, task, action]);

    
    const onCancelClicked = onDismissing;

    const onOpenChanged = useCallback((isOpen: boolean) => {
        if (isOpen) {
            return;
        }

        onDismissing();
    }, [onDismissing]);

    const onSubmitting = useCallback(async () => {
        if (isSaving) {
            return;
        }

        if (model.title.trim().length === 0) {
            return;
        }

        setState((prevState) => ({ ...prevState, isSaving: true }));

        const assigneeIds = model.assignees?.map(a => a.key as number) ?? [];

        let savedAction: Spintr.TaskListTask | undefined;
        try {
            if (!action && !task && listId) {
                savedAction = await createTaskListItemAsync({
                    ...model,
                    title: model.title.trim(),
                    listId: listId,
                    tagIds: [],
                    assigneeIds,
                });
            } else if (task && !action) {
                savedAction = await createTaskListActionAsync(task.id, {
                    ...model,
                    assigneeIds,
                });
            } else if (action) {
                savedAction = await updateTaskListItemAsync(action.id, {
                    ...model,
                    listId: action.listId,
                    tagIds: [],
                    assigneeIds,
                });
            } else {
                console.error("How did we get here..?");
            }
        } catch (err) {
            console.error("Error creating action:", err);
            setState((prevState) => ({ ...prevState, isSaving: false }));
            return;
        }

        setModel({
            ...initialModel,
            contentStatus: isOwnerOfList
                ? SpintrTypes.ContentStatus.Published
                : SpintrTypes.ContentStatus.Draft,
        });
        setState((prevState) => ({ ...prevState, isSaving: false }));
        dispatch(!action
            ? taskListItemCreated(savedAction)
            : taskListItemUpdated(savedAction));
        onDismiss?.(true);
    }, [action, isSaving, model, setState, task?.id]);

    useEffect(() => {
        if (!action) {
            setModel({
                ...initialModel,
                contentStatus: isOwnerOfList
                    ? SpintrTypes.ContentStatus.Published
                    : SpintrTypes.ContentStatus.Draft,
            });
            return;
        }

        setModel({
            ...initialModel,
            ...action,
            assignees: (action.assignments ?? [])
                .map((a) => ({
                    key: a.assignee.id,
                    name: a.assignee.name,
                    imageUrl: a.assignee.imageUrl,
                    type: SpintrTypes.UberType.User,
                })),
        });
    }, [action, open, setModel]);

    const titleText = action?.title
        ? `${localize("Redigera")} '${action.title}'`
        : (!task ? l10n.createAction : localizeFormat("CREATE_FOLLOW_UP_ON_X", task.title));

    return (
        <Drawer className="QualityHubActionFormDrawer"
                onOpenChange={onOpenChanged}
                open={open}
                position="end"
                size={isSmallViewMode ? "full" : "medium"}>
            <DrawerHeader>
                <DrawerHeaderTitle>
                    {titleText}
                </DrawerHeaderTitle>
            </DrawerHeader>
            <DrawerBody>
                <Conditional condition={isSaving}>
                    <SpintrLoader />
                </Conditional>
                <Conditional condition={!isSaving}>
                    <QualityHubActionForm canChangeContentStatus={isLeader || !isOwnerOfList}
                                          canRequireVerification={isLeader || !isOwnerOfList}
                                          listOwnerId={taskList?.targets?.map(t => t.id)?.[0]}
                                          model={model}
                                          onModelChange={onModelChanged} />
                </Conditional>
            </DrawerBody>
            <DrawerFooter className="QualityHubActionFormDrawer-footer">
                <UnstyledButton disabled={isSaving}
                                onClick={onCancelClicked}>
                    <SmallBodyBolder color="contentLight">
                        {l10n.cancel}
                    </SmallBodyBolder>
                </UnstyledButton>
                <PrimaryButton className="QualityHubTaskFormDrawer-save"
                               disabled={isSaving}
                               onClick={onSubmitting}
                               type="submit">
                    {l10n.save}
                </PrimaryButton>
            </DrawerFooter>
        </Drawer>
    );
}

export default QualityHubActionFormDrawer;

